import React from 'react';
import DatePicker from 'react-datepicker';
import uuid from 'uuid/v4';

import 'react-datepicker/dist/react-datepicker.css';
import '../forms.css';
import './date.css';
import { systemText } from '../../../helpers/string';
import FormLabel from '../formLabel';

const DateControl = props => {
	const {
		extraProps,
		inline,
		onChange,
		value,
		label,
		mandatory,
		help,
		error,
		warning,
		id,
		disabled,
		placeholder,
		allowNull,
		className,
		readonly,
		showTime,
		bypassDefault,
		...rest
	} = props;

	const guid = uuid();
	const [year, setYear] = React.useState('');
	const [month, setMonth] = React.useState('');
	const [day, setDay] = React.useState('');
	const [hour, setHour] = React.useState('');
	const [minute, setMinute] = React.useState('');
	const [changed, setChanged] = React.useState(0);

	React.useEffect(() => {
		try {
			if (!bypassDefault && !year) {
				var date = value ? new Date(value) : null;

				if (date) {
					if (showTime) {
						setYear(date.getFullYear());
						setMonth(date.getMonth());
						setDay(date.getDate());
						setHour(date.hour);
						setMinute(date.minute);
					} else {
						date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
						setYear(date.getFullYear());
						setMonth(date.getMonth());
						setDay(date.getDate());
					}
				} else {
					setYear(null);
					setMonth(null);
					setDay(null);
					setHour(null);
					setMinute(null);
				}

				setChanged(changed + 1);
			}
		} catch {
			// invalid date
		}
	}, [value]);

	React.useEffect(() => {
		const pad = x => x.toString().padStart(2, '0');

		if (year) {
			if (showTime) {
				const output = new Date(year, month, day, hour, minute);
				onChange?.(output.toISOString());
			} else {
				onChange?.(`${year}-${pad(month + 1)}-${pad(day)}`);
			}
		}
	}, [changed]);

	const clear = () => {
		setYear(null);
		setMonth(null);
		setDay(null);
		setHour(null);
		setMinute(null);
		onChange?.(null);
	};

	return (
		<div
			key={id}
			data-testid={rest['data-testid']}
			id={id}
			className={`form-group ${error ? 'has-error' : warning ? 'has-warning' : ''}`}
		>
			<FormLabel type="date" {...props} htmlFor={`date-${id || guid}`} />
			<div className="flex flex-center">
				<div className="input-group" style={{ position: 'relative' }}>
					{!inline && (
						<span className="input-group-addon">
							<i className="fa fa-calendar" />
						</span>
					)}
					<DatePicker
						{...extraProps}
						id={`date-${id || guid}`}
						inline={inline}
						disabled={disabled}
						placeholderText={placeholder}
						className={`form-control ${className}`}
						dateFormat={'dd MMM yyyy'}
						selected={year ? new Date(year, month, day) : null}
						onSelect={x => {
							// Only set year, month, day from the selected date, ignore timezone offset
							setYear(x.getFullYear());
							setMonth(x.getMonth());
							setDay(x.getDate());
							setChanged(changed + 1);
						}}
						// onChange={trackChange}
						// onBlur={trackBlur}
						readOnly={readonly}
						popperModifiers={{
							offset: {
								enabled: true,
								offset: '0px, 0px',
							},
							preventOverflow: {
								enabled: true,
								escapeWithReference: false,
								boundariesElement: 'scrollParent',
							},
						}}
					/>
				</div>
				{showTime && (
					<div className="input-group flex flex-center ml10" style={{ position: 'relative', top: '-2px' }}>
						<span className="input-group-addon">
							<i className="fa fa-clock" />
						</span>
						<input
							type="number"
							disabled={disabled}
							min={0}
							max={23}
							maxLength={2}
							value={hour.toString().padStart(2, '0')}
							onChange={x => setHour(x.target.value.toString().padStart(2, '0'))}
							className="form-control time-control"
						/>
						<span className="input-group-addon narrow">:</span>
						<input
							type="number"
							disabled={disabled}
							min={0}
							max={59}
							step={5}
							maxLength={2}
							value={minute.toString().padStart(2, '0')}
							onChange={x => setMinute(x.target.value.toString().padStart(2, '0'))}
							className="form-control time-control"
						/>
					</div>
				)}
			</div>
			{allowNull && value && (
				<div>
					<a className="pointer" onClick={clear}>
						{systemText('General/Controls/Date/Clear')}
					</a>
				</div>
			)}
			{error && <span className="help-block">{typeof error === 'function' ? error() : error}</span>}
			{warning && <span className="help-block">{typeof warning === 'function' ? warning() : warning}</span>}
		</div>
	);
};

export default DateControl;
